import { useEffect, useState } from 'react';
import { mutate } from 'swr';
import { useTranslation } from 'react-i18next';

import { Button } from 'src/components/Buttons/Button';
import CheckBox from 'src/components/FormComponents/CheckBox';
import { useNotification } from 'src/components/Notification/useNotfication';
import container from 'src/container';
import { OrganizationModuleType } from 'src/domain/organization';

const {
    cradle: { munduClient },
} = container;

type ModuleEmployeeReadAccessSettingsProps = {
    organizationId: string;
    moduleType: OrganizationModuleType;
    moduleName: string;
    enabled: boolean;
};

export const ModuleEmployeeReadAccessSettings = ({
    organizationId,
    moduleType,
    moduleName,
    enabled,
}: ModuleEmployeeReadAccessSettingsProps) => {
    const { t } = useTranslation();
    const notify = useNotification();
    const [savedEnabled, setSavedEnabled] = useState(enabled);
    const [draftEnabled, setDraftEnabled] = useState(enabled);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setSavedEnabled(enabled);
        setDraftEnabled(enabled);
    }, [enabled]);

    const hasChanges = draftEnabled !== savedEnabled;

    const save = async () => {
        if (!hasChanges) return;

        setIsSaving(true);
        try {
            await munduClient.updateOrganizationModule(organizationId, {
                type: moduleType,
                employeeReadAccess: draftEnabled,
            });
            await mutate(['organization', organizationId]);
            setSavedEnabled(draftEnabled);
            notify(t('modules.moduleEmployeeReadAccess.updated'));
        } catch (error) {
            if (error instanceof Error) notify(error.message, true);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <section className="rounded-2xl border border-starkWhite bg-white p-5 shadow-sm sm:p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="max-w-2xl">
                    <h2 className="font-logo text-xl">
                        {t('modules.moduleEmployeeReadAccess.title')}
                    </h2>
                    <p className="mt-1 text-sm leading-6 text-mongooseGray">
                        {t('modules.moduleEmployeeReadAccess.description', {
                            module: moduleName,
                        })}
                    </p>
                </div>
                <Button
                    title={
                        isSaving
                            ? t('modules.moduleEmployeeReadAccess.saving')
                            : t('common.save')
                    }
                    onClick={save}
                    disabled={!hasChanges || isSaving}
                    active={hasChanges}
                />
            </div>

            <CheckBox
                className="mt-5"
                label={t('modules.moduleEmployeeReadAccess.label')}
                helperText={t('modules.moduleEmployeeReadAccess.helper')}
                checked={draftEnabled}
                disabled={isSaving}
                onChange={event => setDraftEnabled(event.currentTarget.checked)}
            />
        </section>
    );
};
